// @ts-strict-ignore
import {
    UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION,
    UpdateBucketBuildsPaginationPayload,
    UpdateSharedWithMeBuildsPaginationPayload,
    UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION,
    UpdateBucketBuildWorkItemsTimelinePaginationPayload,
    UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED,
    ReleaseStepDemoData,
    UPDATE_PAGINATION_DATA,
    SET_EVENTS_TIMELINE_DRAWER_VISIBILITY,
} from './../actions/ui';
import { LoadingActionType, LOADING_START, LOADING_STOP, LoadingActionPayloadType } from '../actions/loading';
import { combineReducers } from 'redux';
import {
    UPDATED_START,
    UPDATED_STOP,
    UpdatedActionType,
    UpdatedActionPayloadType,
    WillUpdateActionType,
    WILL_UPDATE_START,
    WILL_UPDATE_STOP,
} from '../actions/updated';
import { uniq, reject, forEach, unset } from 'lodash';
import { FailedActionType, REQUEST_FAILED, CLEAR_FAILURE, FailedPayload } from '../actions/failed';
import {
    PaginationDataMap,
    APPS_ADDED_SHOULD_REDIRECT,
    ClearDemoModeForReleasesAction,
    CLEAR_APPS_DEMO_MODE,
    CLEAR_RELEASE_DEMO_MODE,
    DemoData,
    ReleaseDemoData,
    SetAppsDemoModeAction,
    SetDemoReleaseAction,
    SET_APPS_DEMO_MODE,
    SET_DEMO_RELEASES,
    SET_SHOW_WELCOME_MODAL,
    UiConfig,
    UpdateReleasesPaginationPayload,
    UpdateUIConfigActionType,
    UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED,
    UPDATE_RELEASES_PAGINATION,
    UPDATE_BUCKET_BUILDS_PAGINATION,
} from '../actions/ui';
import { UPDATE_ISSUES_PAGINATION, UpdateIssuesPaginationPayload } from '../actions/triage';
import {
    UpdateWsConnectedActionType,
    REVALIDATE_REALTIME_STATE,
    UPDATE_WS_IS_ATTEMPTING_RECONNECT,
    UPDATE_WS_IS_CONNECTED,
    WsState,
} from '../actions/ws';
import featureItemSearchAndFiltersAndSortReducer, {
    approvalsSortAndSearchReducer,
    usersSearchReducer,
    featureFlagsSortReducer,
    automationsToolboxSortReducer,
    timelineEventFiltersReducer,
} from './filtersAndSort';
import { StringKeyedDictionary } from '../typescriptHelpers';
import publicApiKeyReducer from './publicApi';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import { CommonSagaActionsData } from '@/actions/entities';
import { getPaginatedEntityId } from '@/selectors/selectors';

type FailableActionType = FailedActionType;

const loadingReducer = (
    state: LoadingActionPayloadType[] = [],
    action: LoadingActionType,
): LoadingActionPayloadType[] => {
    switch (action.type) {
        case LOADING_START:
            return [...state, action.payload];
        case LOADING_STOP:
            return reject(state, (payload) => {
                return action.payload.actionName === payload.actionName && action.payload.id === payload.id;
            });
        default:
            return state;
    }
};

// When something is updated that you want to notify a component about
const updatedReducer = (
    state: UpdatedActionPayloadType[] = [],
    action: UpdatedActionType,
): UpdatedActionPayloadType[] => {
    switch (action.type) {
        case UPDATED_START:
            return uniq([...state, action.payload]);
        case UPDATED_STOP:
            return reject(state, (payload) => {
                return action.payload.updatedType === payload.updatedType && action.payload.id === payload.id;
            });
        default:
            return state;
    }
};

// When something is about to update in the store, but you need to do something in the ui before that happens
const willUpdateReducer = (
    state: UpdatedActionPayloadType[] = [],
    action: WillUpdateActionType,
): UpdatedActionPayloadType[] => {
    switch (action.type) {
        case WILL_UPDATE_START:
            return uniq([...state, action.payload]);
        case WILL_UPDATE_STOP:
            return reject(state, (payload) => {
                return action.payload.updatedType === payload.updatedType && action.payload.id === payload.id;
            });
        default:
            return state;
    }
};

const failedReducer = (state: FailedPayload[] = [], action: FailableActionType) => {
    switch (action.type) {
        case REQUEST_FAILED:
            return [...state, action.payload];
        case CLEAR_FAILURE: {
            const updatedState = reject(state, (failedPayload) => {
                return failedPayload.type === action.payload.type && action.payload.id === failedPayload.id;
            });

            return updatedState;
        }
        default:
            return state;
    }
};

const updateViewDemoReleaseForReleases: (
    state: StringKeyedDictionary<ReleaseDemoData>,
    action: SetDemoReleaseAction,
) => StringKeyedDictionary<ReleaseDemoData> = (state, action) => {
    const updatedDemoReleases: StringKeyedDictionary<ReleaseDemoData> = { ...state };

    forEach(action.payload.demoReleases, (demoRelease) => {
        updatedDemoReleases[demoRelease.releaseId] = {
            release: demoRelease.release,
        };
    });

    return updatedDemoReleases;
};

const updateViewDemoReleaseStepsForReleases: (
    state: StringKeyedDictionary<ReleaseStepDemoData>,
    action: SetDemoReleaseAction,
) => StringKeyedDictionary<ReleaseStepDemoData> = (state, action) => {
    const updatedDemoReleaseSteps: StringKeyedDictionary<ReleaseStepDemoData> = { ...state };

    forEach(action.payload.demoReleases, (demoRelease) => {
        forEach(demoRelease.releaseSteps, (releaseStep) => {
            const releaseStepId = generateBackwardsCompatibleReleaseStepRunId(demoRelease.releaseId, releaseStep.type);
            updatedDemoReleaseSteps[releaseStepId] = {
                releaseStep: { ...releaseStep, id: releaseStepId },
            };
        });
    });

    return updatedDemoReleaseSteps;
};

const setDemoAppsData: (action: SetAppsDemoModeAction) => DemoData = (action) => {
    const demoData: DemoData = {
        releaseDemoData: {},
        appsDemoData: {},
        releaseStepsDemoData: {},
        appGroupsDemoData: action.payload.appGroups,
        integrationProviders: [...action.payload.integrationProviders],
        statistics: action.payload.statistics,
        buildDistroDemoData: action.payload.buildDistroDemoData,
        stepRunsDemoData: action.payload.stepRunsDemoData || {},
        stepConfigsDemoData: action.payload.stepConfigsDemoData || {},
        orgDestinations: action.payload.orgDestinations || {},
        orgInsights: action.payload.orgInsights || {},
        summaryPreferences: action.payload.summaryPreferences || {},
        summaryViewByRequestId: action.payload.summaryViewByRequestId || {},
        orgUsersDemoData: action.payload.orgUsersDemoData || {},
        issuesDemoData: action.payload.issuesDemoData || {},
        issueTimelinesDemoData: action.payload.issueTimelinesDemoData || {},
    };

    forEach(action.payload.releases, (demoRelease) => {
        demoData.releaseDemoData[demoRelease.releaseId] = {
            release: demoRelease.release,
        };
    });

    forEach(action.payload.releases, (demoRelease) => {
        forEach(demoRelease.releaseSteps, (releaseStep) => {
            const releaseStepId = generateBackwardsCompatibleReleaseStepRunId(demoRelease.releaseId, releaseStep.type);
            demoData.releaseStepsDemoData[releaseStepId] = {
                releaseStep: { ...releaseStep, id: releaseStepId },
            };
        });
    });

    forEach(action.payload.apps, (demoAppData) => {
        demoData.appsDemoData[demoAppData.app.appId] = { ...demoAppData };
    });

    return demoData;
};

const clearDemoReleaseForReleases: (
    state: StringKeyedDictionary<ReleaseDemoData>,
    action: ClearDemoModeForReleasesAction,
) => StringKeyedDictionary<ReleaseDemoData> = (state, action) => {
    const updatedDemoReleases = {
        ...state,
    };

    forEach(action.payload.releaseIds, (releaseId) => {
        unset(updatedDemoReleases, releaseId);
    });

    return updatedDemoReleases;
};

const clearDemoReleaseStepsForReleases: (
    state: StringKeyedDictionary<ReleaseStepDemoData>,
    action: ClearDemoModeForReleasesAction,
) => StringKeyedDictionary<ReleaseStepDemoData> = (state, action) => {
    const updatedDemoReleases = {
        ...state,
    };

    forEach(action.payload.releaseIds, (releaseId) => {
        forEach(updatedDemoReleases, (releaseStep) => {
            const releaseStepId = generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStep.releaseStep.type);
            unset(updatedDemoReleases, releaseStepId);
        });
    });

    return updatedDemoReleases;
};

const updateReleasePagination: (
    state: PaginationDataMap,
    payload: UpdateReleasesPaginationPayload,
) => PaginationDataMap = (state, payload) => {
    const currOffset = payload.offset;
    const updatedOffset = currOffset + payload.currentCount;

    return {
        ...state,
        [payload.appId]: {
            limit: payload.limit,
            offset: updatedOffset,
            totalCount: payload.totalCount,
        },
    };
};

// Stores the offset that was just fetched (not the next one): the Triage list is numbered-paginated, so
// the current page reads back as offset / limit.
const updateIssuesPaginationState: (
    state: PaginationDataMap,
    payload: UpdateIssuesPaginationPayload,
) => PaginationDataMap = (state, payload) => ({
    ...state,
    [payload.appId]: {
        limit: payload.limit,
        offset: payload.offset,
    },
});

const updateSharedWithMeBuildsPagination: (
    state: PaginationDataMap,
    payload: UpdateSharedWithMeBuildsPaginationPayload,
) => PaginationDataMap = (state, payload) => {
    const currOffset = payload.offset;
    const updatedOffset = currOffset + payload.currentCount;

    return {
        ...state,
        [payload.appId]: {
            limit: payload.limit,
            offset: updatedOffset,
            totalCount: payload.totalCount,
        },
    };
};

const updateBucketBuildsPagination: (
    state: PaginationDataMap,
    payload: UpdateBucketBuildsPaginationPayload,
) => PaginationDataMap = (state, payload) => {
    return {
        ...state,
        [payload.bucketId]: {
            limit: payload.limit,
            nextCursor: payload.nextCursor,
        },
    };
};

const updateBucketBuildsWorkItemsTimelinePagination: (
    state: PaginationDataMap,
    payload: UpdateBucketBuildWorkItemsTimelinePaginationPayload,
) => PaginationDataMap = (state, payload) => {
    const currOffset = payload.offset;
    const updatedOffset = currOffset + payload.currentCount;

    return {
        ...state,
        [payload.buildId]: {
            limit: payload.limit,
            offset: updatedOffset,
            totalCount: payload.totalCount,
        },
    };
};

const updatePaginationData: (state: PaginationDataMap, action: CommonSagaActionsData) => PaginationDataMap = (
    state,
    action,
) => {
    const entityId = getPaginatedEntityId(action.type, action.payload.id);
    return {
        ...state,
        [entityId]: action.paginationData,
    };
};

const initialState: UiConfig = {
    runwayAdminLiveModeEnabled: false,
    runwayAdminWebsocketsEnabled: true,
    welcomeModalOptions: {
        shouldShowWelcomeModal: false,
    },
    appsAddedSuccess: [],
    demoData: null,
    releasesPagination: {},
    bucketBuildsPagination: {},
    sharedWithMeBuildsPagination: {},
    bucketBuildsWorkItemsTimelinePagination: {},
    issuesPagination: {},
    paginationData: {},
    eventsTimelineDrawer: {
        stepRunId: null,
        isVisible: false,
    },
};

const configReducer: (state: UiConfig, action: UpdateUIConfigActionType) => UiConfig = (
    state: UiConfig = initialState,
    action,
) => {
    switch (action.type) {
        case UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED:
            return {
                ...state,
                runwayAdminLiveModeEnabled: action.payload.enabled,
            };
        case UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED:
            return {
                ...state,
                runwayAdminWebsocketsEnabled: action.payload.enabled,
            };
        case SET_DEMO_RELEASES:
            return {
                ...state,
                demoData: {
                    releaseDemoData: updateViewDemoReleaseForReleases(state.demoData?.releaseDemoData ?? {}, action),
                    releaseStepsDemoData: updateViewDemoReleaseStepsForReleases(
                        state.demoData?.releaseStepsDemoData ?? {},
                        action,
                    ),
                },
            };
        case CLEAR_RELEASE_DEMO_MODE:
            return {
                ...state,
                demoData: {
                    releaseDemoData: clearDemoReleaseForReleases(state.demoData?.releaseDemoData ?? {}, action),
                    releaseStepsDemoData: clearDemoReleaseStepsForReleases(
                        state.demoData?.releaseStepsDemoData ?? {},
                        action,
                    ),
                },
            };
        case SET_APPS_DEMO_MODE:
            return {
                ...state,
                demoData: setDemoAppsData(action),
            };
        case CLEAR_APPS_DEMO_MODE:
            return {
                ...state,
                demoData: null,
            };
        case SET_SHOW_WELCOME_MODAL:
            return {
                ...state,
                welcomeModalOptions: action.payload,
            };
        case APPS_ADDED_SHOULD_REDIRECT:
            return {
                ...state,
                appsAddedSuccess: action.payload.appIdParams,
            };
        case UPDATE_RELEASES_PAGINATION:
            return {
                ...state,
                releasesPagination: updateReleasePagination(state.releasesPagination, action.payload),
            };
        case UPDATE_BUCKET_BUILDS_PAGINATION:
            return {
                ...state,
                bucketBuildsPagination: updateBucketBuildsPagination(state.bucketBuildsPagination, action.payload),
            };
        case UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION:
            return {
                ...state,
                sharedWithMeBuildsPagination: updateSharedWithMeBuildsPagination(
                    state.sharedWithMeBuildsPagination,
                    action.payload,
                ),
            };
        case UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION: {
            return {
                ...state,
                bucketBuildsWorkItemsTimelinePagination: updateBucketBuildsWorkItemsTimelinePagination(
                    state.bucketBuildsWorkItemsTimelinePagination,
                    action.payload,
                ),
            };
        }
        case UPDATE_ISSUES_PAGINATION: {
            return {
                ...state,
                issuesPagination: updateIssuesPaginationState(state.issuesPagination, action.payload),
            };
        }
        case UPDATE_PAGINATION_DATA: {
            return {
                ...state,
                paginationData: updatePaginationData(state.paginationData, action),
            };
        }
        case SET_EVENTS_TIMELINE_DRAWER_VISIBILITY: {
            return {
                ...state,
                eventsTimelineDrawer: {
                    stepRunId: action.payload.stepRunId,
                    isVisible: action.payload.isVisible,
                },
            };
        }
        default:
            return state;
    }
};

const initialWsState: WsState = {
    hasPreviouslyOpenedConnection: false,
    isConnected: false,
    isAttemptingReconnect: false,
    revalidationSequence: 0,
};

const wsStateReducer = (state: WsState = initialWsState, action: UpdateWsConnectedActionType) => {
    switch (action.type) {
        case UPDATE_WS_IS_CONNECTED:
            return {
                ...state,
                isConnected: action.payload.isConnected,
                hasPreviouslyOpenedConnection: true,
            };
        case UPDATE_WS_IS_ATTEMPTING_RECONNECT:
            return {
                ...state,
                isAttemptingReconnect: action.payload.isAttemptingReconnect,
            };
        case REVALIDATE_REALTIME_STATE:
            return {
                ...state,
                revalidationSequence: state.revalidationSequence + 1,
            };
        default:
            return state;
    }
};

const uiReducer = combineReducers({
    featureItemSearchAndFiltersAndSorts: featureItemSearchAndFiltersAndSortReducer,
    automationsToolboxSorts: automationsToolboxSortReducer,
    featureFlagsSorts: featureFlagsSortReducer,
    approvalsSearchAndSorts: approvalsSortAndSearchReducer,
    userSearch: usersSearchReducer,
    loading: loadingReducer,
    willUpdate: willUpdateReducer,
    updated: updatedReducer,
    failed: failedReducer,
    uiConfig: configReducer,
    wsState: wsStateReducer,
    publicApiKey: publicApiKeyReducer,
    timelineEventFilters: timelineEventFiltersReducer,
});

export default uiReducer;
